import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../utils/axios';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

export const LogoUploader = ({ value, onChange, folder = 'media', label = 'Logo', dataTestId }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const previewUrl = value
    ? (value.startsWith('http') ? value : `${BACKEND_ORIGIN}${value}`)
    : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('File harus berupa gambar');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran maksimal 5MB');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    setUploading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/admin/media/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast.success('Logo berhasil diupload');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Gagal upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const clearLogo = () => onChange('');

  return (
    <div>
      <label className="block text-white text-sm font-semibold mb-2">{label}</label>
      <div className="flex items-start gap-3">
        <div
          className="w-24 h-24 rounded-lg border border-white/20 bg-white/5 flex items-center justify-center overflow-hidden flex-shrink-0"
          data-testid={`${dataTestId}-preview`}
        >
          {previewUrl ? (
            <img src={previewUrl} alt="Preview" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon className="text-gray-500" size={32} />
          )}
        </div>

        <div className="flex-1 space-y-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            className="hidden"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex-1 px-3 py-2 rounded-lg bg-[#D90429] hover:bg-[#B00320] text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid={`${dataTestId}-upload-btn`}
            >
              <Upload size={16} />
              {uploading ? 'Mengupload...' : (value ? 'Ganti Logo' : 'Upload Logo')}
            </button>
            {value && (
              <button
                type="button"
                onClick={clearLogo}
                className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-1"
                data-testid={`${dataTestId}-clear-btn`}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="atau tempel URL gambar..."
            className="text-xs"
            data-testid={`${dataTestId}-url-input`}
          />
          <p className="text-gray-500 text-xs">PNG, JPG, WEBP, SVG. Maks 5MB.</p>
        </div>
      </div>
    </div>
  );
};
